import React from 'react';
import { Mail, Linkedin } from 'lucide-react';
import davidPortrait from '@/assets/partner-david-ochieng.jpg';
import SEO from '../../components/SEO';

const DavidOchieng = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="David Ochieng — Law and Code Africa"
        description="David Ochieng at Law and Code Africa. LLB Hons, CPM, Trainee Advocate. Focused on AI law, intellectual property law, and technology policy."
        path="/partners/david-ochieng"
      />

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">David Ochieng</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | CPM | Trainee Advocate | Moot Court Champion
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                David Ochieng is a forward-looking legal analyst with primary areas of interest in AI Law, Intellectual Property Law, and Technology Policy. A Trainee Advocate currently enrolled at the Kenya School of Law, he is passionate about the regulatory frameworks that will govern autonomous systems, machine-generated works, and algorithmic decision-making across the African continent.
              </p>
              <p>
                He has demonstrated a capacity for translating complex technical concepts into accessible legal commentary, producing analysis on the EU AI Act, copyright in generative-AI outputs, and the liability regimes for autonomous systems. Through academic publication, panel discussions, and active mentorship of younger law students, he contributes to the emerging AI-law community in East Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6 bg-gray-50">
                <img
                  src={davidPortrait}
                  alt="David Ochieng - Partner, Law and Code Africa"
                  loading="lazy"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-5 md:p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-lg md:text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="text-secondary flex-shrink-0" size={20} />
                    <span className="text-gray-700 text-sm md:text-base break-all">lawandcodeafrica@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <Linkedin className="text-secondary flex-shrink-0" size={20} />
                    <a
                      href="https://www.linkedin.com/in/david-ochieng-law"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm md:text-base break-all"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Kabarak University School of Law (LLB Hons):</strong> Attended Kabarak University where he developed expertise in intellectual property, technology law, and international trade law. During his tenure, he captained the Kabarak Moot Court Team to the finals of the East African Moot Court Competition and served as Editor-in-Chief of the Kabarak Law Journal.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an Advocates Training Programme (ATP) student.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Professional Mediator</h4>
                    <p className="text-sm text-gray-600">Holds certification as a Professional Mediator</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">IP &amp; Technology Analyst</h4>
                    <p className="text-sm text-gray-600">Skilled in patent landscapes, copyright licensing, and technology-transfer agreements</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Policy Researcher</h4>
                    <p className="text-sm text-gray-600">Experienced in legislative drafting, regulatory-impact analysis, and tech-policy briefs</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience &amp; Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    <strong>African Innovation Hub:</strong> Volunteers as a Policy Fellow, drafting position papers on AI governance, open-source licensing, and the protection of traditional knowledge in digital environments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            David Ochieng's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">AI Law</h3>
              <p className="text-gray-600">
                Algorithmic accountability, liability for autonomous systems, the EU AI Act, and the development of national AI regulatory frameworks.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Intellectual Property Law</h3>
              <p className="text-gray-600">
                Copyright in AI-generated works, patent protection for software innovations, technology licensing, and traditional-knowledge safeguarding.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Technology Policy</h3>
              <p className="text-gray-600">
                Regulatory design for emerging technologies, public-interest litigation in tech governance, and the interface between innovation and constitutional rights.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DavidOchieng;
